import { shouldAnimate } from '$digerati/accessibility/reducedMotion';
import { eventBus } from '$digerati/events';
import { autoGroup, devError, error as logError, log, warn } from '$digerati/utils/logger';

/**
 * Detect Safari (but not Chrome on macOS/iOS)
 */
const isSafari =
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
  (navigator.vendor && navigator.vendor.indexOf('Apple') > -1 && !window.hasOwnProperty('chrome'));

/**
 * Testimonial Avatar Halftone effect tied to scroll visibility.
 */
let initialized = false;

export const testimonialAvatar = async () => {
  if (initialized) return;
  autoGroup('Testimonial Avatar', () => {
    eventBus.emit('testimonialAvatar:init');
    // Respect reduced motion preference
    if (!shouldAnimate()) {
      log('Reduced motion requested; skipping testimonial avatar halftone effects.');
      eventBus.emit('testimonialAvatar:exit', { alt: undefined });
      return;
    }

    // Skip Safari only during local development to avoid mixed-content errors
    if (isSafari && location.hostname === 'localhost') {
      warn('Safari detected in local dev; skipping testimonial avatar halftone effects.');
      eventBus.emit('testimonialAvatar:exit', { alt: undefined });
      return;
    }

    const avatars = Array.from(
      document.querySelectorAll<HTMLImageElement>('.testimonial_half-tone')
    );

    if (!avatars.length) {
      warn('No testimonial avatars found.');
      return;
    }

    log(`Found ${avatars.length} testimonial avatar(s).`);

    const initIfNeeded = (img: HTMLImageElement) => {
      if ((img as any)._halftone) return;
      try {
        initHalftone(img);
        if ((img as any)._halftone) eventBus.emit('testimonialAvatar:enter', { alt: img.alt });
      } catch (e) {
        logError('Failed to initialize halftone for image:', img.alt || '(no alt)', e);
        eventBus.emit('testimonialAvatar:error', {
          message: 'halftone-init-failure',
          alt: img.alt,
        });
      }
    };

    const destroyIfExists = (img: HTMLImageElement) => {
      if ((img as any)._halftone) {
        destroyHalftone(img);
        eventBus.emit('testimonialAvatar:exit', { alt: img.alt });
      }
    };

    // Halftone sets its image to display:none. Observe the fixed-size wrapper instead.
    const avatarsByContainer = new Map<Element, HTMLImageElement[]>();
    avatars.forEach((img) => {
      const container = img.closest(
        '.testimonial_half-tone-wrapper, .home_case-study_half-tone-wrapper'
      );
      if (!container) {
        logError('No stable halftone wrapper found for', img.alt || '(no alt)');
        eventBus.emit('testimonialAvatar:error', { message: 'missing-wrapper', alt: img.alt });
        return;
      }
      const images = avatarsByContainer.get(container) ?? [];
      images.push(img);
      avatarsByContainer.set(container, images);
    });

    // Equivalent to top/bottom viewport boundaries, with no preloading margin.
    let observing = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!observing) return;
        entries.forEach((entry) => {
          avatarsByContainer.get(entry.target)?.forEach((img) => {
            if (entry.isIntersecting) initIfNeeded(img);
            else destroyIfExists(img);
          });
        });
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );
    initialized = true;
    avatarsByContainer.forEach((_, container) => observer.observe(container));
    window.addEventListener('pagehide', () => {
      observing = false;
      observer.disconnect();
      avatars.forEach(destroyIfExists);
    });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      observing = true;
      avatarsByContainer.forEach((_, container) => observer.observe(container));
    });

    eventBus.emit('testimonialAvatar:initialized', { count: avatars.length });
  });
};

/**
 * Initialize the halftone on the image.
 */
const initHalftone = (img: HTMLImageElement) => {
  log('Initializing halftone for', img.alt || '(no alt)');

  const HalftoneConstructor =
    (window as any).BreathingHalftone ?? (globalThis as any).BreathingHalftone;

  if (typeof HalftoneConstructor === 'undefined') {
    devError('BreathingHalftone constructor is not available.');
    eventBus.emit('testimonialAvatar:error', {
      message: 'BreathingHalftone missing',
      alt: img.alt,
    });
    return;
  }

  const instance = new HalftoneConstructor(img, {
    dotSize: 1 / 70,
    dotSizeThreshold: 0.025,
    initVelocity: 0.5,
    oscPeriod: 3,
    oscAmplitude: 0.2,
    isAdditive: false,
    isRadial: false,
    channels: ['lum'],
    isChannelLens: true,
    friction: 0.06,
    hoverDiameter: 0.3,
    hoverForce: -0.02,
    activeDiameter: 0.6,
    activeForce: 0.01,
  });

  (img as any)._halftone = instance;
};

/**
 * Destroy the halftone instance if present.
 */
const destroyHalftone = (img: HTMLImageElement) => {
  if ((img as any)._halftone) {
    log('Destroying halftone for', img.alt || '(no alt)');
    try {
      (img as any)._halftone.destroy();
    } catch (e) {
      warn('Error while destroying halftone instance:', e);
    }
    delete (img as any)._halftone;
    eventBus.emit('testimonialAvatar:destroyed', { alt: img.alt });
  }
};
